import { parseDbTimestamp } from "@repo/shared";
import { useMemo } from "react";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./use-dashboard-format.i18n";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Three significant digits: 412, 18.4, 5.16. */
function significantDecimals(value: number): number {
  const abs = Math.abs(value);
  return abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
}

/** A moment of the viewer's own local day, for "Today" / "Tomorrow". */
function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

type Formatters = {
  relative: Intl.RelativeTimeFormat;
  time: Intl.DateTimeFormat;
  day: Intl.DateTimeFormat;
  shortDate: Intl.DateTimeFormat;
};

// Built once per language and shared by every widget, not once per widget.
const formattersByLocale = new Map<string, Formatters>();

function formattersFor(locale: string): Formatters {
  let formatters = formattersByLocale.get(locale);
  if (!formatters) {
    formatters = {
      relative: new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" }),
      time: new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }),
      day: new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric" }),
      shortDate: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }),
    };
    formattersByLocale.set(locale, formatters);
  }
  return formatters;
}

/**
 * The dashboard's number, money and time formatting: numbers in the tenant's
 * separators, words in the viewer's language. Timestamps arrive as the API's
 * UTC strings and are shown in the viewer's local time.
 */
export function useDashboardFormat() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();

  return useMemo(() => {
    const {
      relative: relativeFormat,
      time: timeFormat,
      day: dayFormat,
      shortDate: shortDateFormat,
    } = formattersFor(userLanguage.replace("_", "-"));

    const toDate = (value: string) => parseDbTimestamp(value) ?? new Date(value);

    const count = (value: number) => formatNumber(value, { maximumFractionDigits: 0 });

    /** A whole amount with thousand separators: 4,120,000. */
    const amount = (value: number) => formatNumber(Math.round(value), { maximumFractionDigits: 0 });

    /** A key-figure amount: NOK 412 M, NOK 1.28 bn, NOK 850,000. */
    const compactMoney = (value: number, currency: string) => {
      const abs = Math.abs(value);
      if (abs >= 1e9) {
        const v = value / 1e9;
        return `${currency} ${formatNumber(v, { maximumFractionDigits: significantDecimals(v) })} ${t.billion}`;
      }
      if (abs >= 1e6) {
        const v = value / 1e6;
        return `${currency} ${formatNumber(v, { maximumFractionDigits: significantDecimals(v) })} ${t.million}`;
      }
      return `${currency} ${amount(value)}`;
    };

    /** A 0–1 share as "7.9 %". */
    const percent = (share: number) =>
      `${formatNumber(share * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

    /** "5 hr. ago", "in 21 hr.", "2 days ago" — past or future. */
    const relative = (value: string, now: Date = new Date()) => {
      const diff = toDate(value).getTime() - now.getTime();
      const abs = Math.abs(diff);
      if (abs < HOUR) return relativeFormat.format(Math.round(diff / MINUTE), "minute");
      if (abs < DAY) return relativeFormat.format(Math.round(diff / HOUR), "hour");
      return relativeFormat.format(Math.round(diff / DAY), "day");
    };

    /** A meeting's day: Today, Tomorrow, or "Wed 17". */
    const meetingDay = (value: string, now: Date = new Date()) => {
      const date = toDate(value);
      const days = Math.round((startOfLocalDay(date) - startOfLocalDay(now)) / DAY);
      if (days === 0) return t.today;
      if (days === 1) return t.tomorrow;
      return dayFormat.format(date);
    };

    const time = (value: string) => timeFormat.format(toDate(value));
    const shortDate = (value: string) => shortDateFormat.format(toDate(value));

    return {
      toDate,
      count,
      amount,
      compactMoney,
      percent,
      relative,
      meetingDay,
      time,
      shortDate,
    };
  }, [userLanguage, t, formatNumber]);
}
